import { UAParser } from 'ua-parser-js'

import type {
  AttributeValueType,
  TransformAttrsArgs,
  TransformedDataAttr,
} from './types'

export const transformAttrs = (
  attrs: TransformAttrsArgs
): TransformedDataAttr[] => {
  return Object.keys(attrs).map((key) => {
    const value = attrs[key]
    let stringValue: string | undefined
    let intValue: number | undefined
    let floatValue: number | undefined
    let doubleValue: number | undefined
    let boolValue: boolean | undefined

    if (!Array.isArray(value)) {
      stringValue = value?.toString()
    } else {
      const strValue = value[0]?.toString()
      const type = value[1]

      if (type === undefined) {
        stringValue = strValue
      } else if (type === 'string') {
        stringValue = strValue
      } else if (type === 'int' && strValue !== '') {
        intValue = parseInt(strValue, 10)
        if (intValue === -0) intValue = 0
        if (isNaN(intValue)) intValue = undefined
      } else if (type === 'float' && strValue !== '') {
        floatValue = parseFloat(strValue)
        if (isNaN(floatValue)) floatValue = undefined
      } else if (type === 'double' && strValue !== '') {
        doubleValue = parseFloat(strValue)
        if (isNaN(doubleValue)) doubleValue = undefined
      } else if (type === 'bool' && strValue !== '') {
        boolValue = Boolean(value[0])
        if (typeof value[0] !== 'boolean') boolValue = undefined
      }
    }

    return {
      key,
      value: {
        string_value: stringValue,
        int_value: intValue,
        float_value: floatValue,
        double_value: doubleValue,
        bool_value: boolValue,
      },
    }
  })
}

export const getPlatform = (androidHandler?: {
  enabled?: boolean
}): 'APP' | 'WEB' => {
  return androidHandler?.enabled ? 'APP' : 'WEB'
}

const SESSION_ID_KEY = 'satu_event_session_id'
const DEVICE_ID_KEY = 'satu_event_device_id'

export const getOrCreateSessionId = (idFactory: () => string): string => {
  if (typeof window === 'undefined') {
    return idFactory()
  }

  try {
    const existingId = window.sessionStorage.getItem(SESSION_ID_KEY)
    if (existingId) {
      return existingId
    }

    const newId = idFactory()
    window.sessionStorage.setItem(SESSION_ID_KEY, newId)
    return newId
  } catch {
    // Fallback if sessionStorage is not available
    return idFactory()
  }
}

export const getOrCreateDeviceId = (
  idFactory: () => string
): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    const existingId = window.localStorage.getItem(DEVICE_ID_KEY)
    if (existingId) {
      return existingId
    }

    const newId = idFactory()
    window.localStorage.setItem(DEVICE_ID_KEY, newId)
    return newId
  } catch {
    // Fallback if localStorage is not available
    return undefined
  }
}

export const buildDefaultData = (
  idFactory: () => string,
  dateFactory: () => Date,
  deviceIdFactory: () => string | undefined,
  userIdFactory: () => string | undefined
) => ({
  id: idFactory(),
  timestamp: dateFactory().toISOString(),
  device_id: deviceIdFactory(),
  user_id: userIdFactory(),
})

export const getDeviceInfoFromUA = () => {
  if (typeof window === 'undefined') {
    return {
      device_model: undefined,
      os_version: undefined,
      app_version: undefined,
    }
  }
  const uap = new UAParser()
  const { browser, device, os } = uap.getResult()
  return {
    browser_name: browser.name,
    device_model: device.model || device.vendor || undefined,
    os: os.name,
  }
}

export type { AttributeValueType, TransformAttrsArgs, TransformedDataAttr }
